import { useId, useState } from "react";

import {
  AppDialog,
  AppDialogActions,
  DialogActionsSpacer,
  DialogPrimaryButton,
  DialogSecondaryButton,
  DialogTertiaryButton,
} from "@/components/ui/app-dialog";
import { focusPaperThenFirstInput } from "@/components/ui/dialog-focus";
import { OutlinedMenuSelectField } from "@/components/ui/outlined-menu-select-field";
import { RichTextField } from "@/components/ui/rich-text-field";
import { REQUIRED_MESSAGE, specNameError } from "@/components/ui/spec-name-rules";
import { TextField } from "@/components/ui/text-field";

// The donor's spec line form — `SpecSheetLineDialogForm`
// (`components/specSheets/SpecSheetLineDialogForm.tsx`), the one form behind
// the Editor's Add Specification Line, Duplicate Specification Line and Edit
// specification line (#807). Shared in the source beside the name-and-note form
// (`name-note-dialog.tsx`), so shared here (`docs/agents/migration-guidelines.md`,
// "Shared donor components port as shared Atlas components").
//
// **The frame is the raw MUI dialog the form draws, not react-ui's shell**,
// measured on the running donor (#807 E1–E4): the 600px `fullWidth` paper; the
// `h2` title alone at `px 40 py 24`, so 88px tall; the close X — named
// `close`, in `grey[500]` — pinned to the paper's corner at `top 24, right
// 32`; the content at `p 40` with `pt 16`; react-ui's action row. The first
// input is focused 50ms after the paper has entered (`focusFirstInput`, which
// skips MUI's invisible native select input, so `Line Name` in every mode), and
// no focus is handed back on the close (`disableRestoreFocus`).
//
// **The mode decides** the title, whether the `Group` select is locked (add and
// duplicate) or open (edit), whether `Notes` and `Clear Note` exist (edit only),
// and the submit's label (`Save` in edit, `Add specification line` otherwise —
// so the duplicate's submit reads as an add, as the donor's does).
//
// **The fields** (`spacing={2}`): `Group *`, required; `Line Name *` with
// react-hook-form's two rules — required, "This field is required"; at most
// 50, "Max length is 50 characters", a rule and not a `maxlength` — checked on
// submit and, once a submit has been tried, on every change; then `Notes`.
// Every field is held while saving.
//
// **The actions**: `Clear Note` (edit only, pushed left by a spacer, never
// held), `Cancel`, then the submit, loading while saving. **Both `Cancel` and
// the submit are held while saving or once the name has been emptied** — the
// donor's `isLineNameEmpty`: `false` to start, set on every name change to
// `value.trim() === ''`, never reset by a re-seed or a close. It holds Cancel
// too, unlike the name-and-note form's flag; the X, Escape and the backdrop
// still reach `onOpenChange`.
//
// **The values** belong to the form, as the donor's `useForm` does: written in
// whenever `seed` is a new object, kept across a close otherwise, and submitted
// as typed — no trim. **Dismissals are reported, never decided here**: the
// donor's Add refuses them while saving and its Edit and Duplicate do not, so
// the caller guards `onOpenChange` or passes it straight through.

export type SpecLineDialogMode = "add" | "duplicate" | "edit";

export interface SpecLineValues {
  /** The `Group` select's group; `null` for none. */
  groupId: number | null;
  name: string;
  notes: string;
}

export interface SpecLineDialogGroup {
  id: number;
  name: string;
}

export interface SpecLineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: SpecLineDialogMode;
  /** The `Group` select's options, in the order they are listed. */
  groups: SpecLineDialogGroup[];
  /** The write is in flight: the submit loads; the fields and Cancel are held. */
  saving?: boolean;
  /** What the form shows — written in whenever this object changes. */
  seed: SpecLineValues;
  onSubmit: (values: SpecLineValues) => void;
  /** The paper has gone — the donor's `onExited`. */
  onExited?: () => void;
}

const TITLES: Record<SpecLineDialogMode, string> = {
  add: "Add Specification Line",
  duplicate: "Duplicate Specification Line",
  edit: "Edit specification line",
};

function groupError(groupId: number | null): string | undefined {
  return groupId === null ? REQUIRED_MESSAGE : undefined;
}

export function SpecLineDialog({
  open,
  onOpenChange,
  mode,
  groups,
  saving = false,
  seed,
  onSubmit,
  onExited,
}: SpecLineDialogProps) {
  const formId = useId();
  const [values, setValues] = useState(seed);
  const [isLineNameEmpty, setIsLineNameEmpty] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // The donor's `reset(…)` in its callers' `useEffect([line | group])` and in
  // Add's exit: a new seed object is written in, whatever was typed, and — as
  // react-hook-form's `reset` does — the errors and the tried submit go with
  // it, so the next check is the next submit's. The emptied-name flag is the
  // form component's own state and stays. Adjusted during render rather than
  // in an effect.
  const [seeded, setSeeded] = useState(seed);
  if (seeded !== seed) {
    setSeeded(seed);
    setValues(seed);
    setSubmitted(false);
  }

  const isEdit = mode === "edit";
  const nameMessage = submitted ? specNameError(values.name) : undefined;
  const groupMessage = submitted ? groupError(values.groupId) : undefined;
  const held = saving || isLineNameEmpty;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={TITLES[mode]}
      size="sm"
      frame="raw"
      onOpenAutoFocus={focusPaperThenFirstInput}
      restoreFocus={false}
      onCloseAutoFocus={() => onExited?.()}
      actions={
        <AppDialogActions>
          {isEdit ? (
            <>
              <DialogTertiaryButton
                onClick={() => setValues((current) => ({ ...current, notes: "" }))}
              >
                Clear Note
              </DialogTertiaryButton>
              <DialogActionsSpacer />
            </>
          ) : null}
          <DialogSecondaryButton disabled={held} onClick={() => onOpenChange(false)}>
            Cancel
          </DialogSecondaryButton>
          <DialogPrimaryButton
            type="submit"
            form={formId}
            loading={saving}
            disabled={isLineNameEmpty}
          >
            {isEdit ? "Save" : "Add specification line"}
          </DialogPrimaryButton>
        </AppDialogActions>
      }
    >
      <form
        id={formId}
        noValidate
        autoComplete="off"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (held) return;
          if (specNameError(values.name) !== undefined || groupError(values.groupId) !== undefined)
            return;
          onSubmit(values);
        }}
      >
        <OutlinedMenuSelectField
          label="Group"
          accessibleName="Group"
          required
          value={values.groupId === null ? "" : String(values.groupId)}
          options={groups.map((group) => ({ value: String(group.id), label: group.name }))}
          onValueChange={(next) => setValues((current) => ({ ...current, groupId: Number(next) }))}
          disabled={!isEdit || saving}
          error={groupMessage}
        />
        <TextField
          label="Line Name"
          required
          value={values.name}
          onChange={(name) => {
            setIsLineNameEmpty(name.trim() === "");
            setValues((current) => ({ ...current, name }));
          }}
          disabled={saving}
          error={nameMessage}
          className="w-full"
        />
        {isEdit ? (
          <RichTextField
            label="Notes"
            disabled={saving}
            value={values.notes}
            onChange={(notes) => setValues((current) => ({ ...current, notes }))}
          />
        ) : null}
      </form>
    </AppDialog>
  );
}
